import { useCallback, useMemo, useState, useRef, useEffect, lazy } from 'react';
import { cn, ButtonTabs } from '@divlab/divanui';
import { useInView } from 'react-intersection-observer';

import * as Configurator from '@Stores/Configurator';
import * as Filtrator from '@Stores/Filtrator';
import * as ConfiguratorQuery from '@Queries/Configurator';
import * as PageProductStore from '@Stores/PageProduct';
import * as HeaderStore from '@Stores/Header';
import * as ModalWindows from '@Stores/ModalWindows';
import * as Meta from '@Queries/Meta';
import { useUpdateComponentStorage } from '@Stores/ComponentStorage';
import Suspense from '@Components/Suspense';
import ErrorBoundary from '@Components/ErrorBoundary';
import CrossSaleProductCard from '@Components/CrossSaleProductCard';
import NanoProductCard from '@Components/NanoProductCard';
import MainInstagramSection from '@Components/MainInstagramSection';
import BreadcrumbsWrapper from '@Components/BreadcrumbsWrapper';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import useMedias from '@Hooks/useMedias';
import useRenderType from '@Hooks/useRenderType';
import useSearchParams from '@Hooks/useSearchParams';
import useTranslation from '@Queries/useTranslation';
import CrossSaleSection from '@Components/CrossSaleSection';
import Sidebar from './elements/Sidebar';
import ComfortBuy from './elements/ComfortBuy';
import ReviewsSection from '@Pages/PageProduct/elements/ReviewsSection';
import ListReviews from '@Pages/PageProduct/elements/ListReviews';
import EmptyReview from '@Pages/PageProduct/elements/ReviewsSection/elements/EmptyReview';
import useNavigation from '@Navigation/useNavigation';
import PhotoGallery from './elements/PhotoGallery';
import ModulesList from './elements/ModulesList';
import DeliverySection from './elements/DeliverySection';
import ATRInfo from './elements/ATRInfo';
import ProductsInInterior from './elements/ProductsInInterior';
import NewCollectionAnnounce from './elements/NewCollectionAnnounce';
import Layout from './elements/Layout';
import ActionBar from './elements/ActionBar';
import StickyBuyButton from './elements/StickyBuyButton';
import AdditionalInfo from './elements/AdditionalInfo';
import QualityLaboratoryBanner from './elements/QualityLaboratoryBanner';
import SaleSoonButton from './elements/SaleSoonButton';
import styles from './PageProduct.module.css';

import type { FC, HTMLAttributes, MutableRefObject, MouseEvent } from 'react';
import type { Tab } from '@divlab/divanui';
import type { ReviewData } from '@Types/Review';
import type { ProductData } from '@Types/Product';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';
import type { AR } from '@Types/AR';
import type { SelectedReviewsData } from '@Pages/PageProduct/elements/ReviewsSection';

export interface PageProductProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const PanelOperator = lazy(() => import('@Components/Operator/PanelOperator'));

const PageProduct: FC<PageProductProps> = (props) => {
  const { className, ...restProps } = props;
  const page = PageProductStore.useData();
  const { isMobileM, isOnlyMobile } = useMedias();
  const { isCSR } = useRenderType();
  const { totalHeight, contentHeight } = HeaderStore.useSizes();
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const [selectedCrossSaleTab, setSelectedCrossSaleTab] = useState('all');
  const [selectedUpSaleTab, setSelectedUpSaleTab] = useState('all');
  const [selectedReviews, setSelectedReviews] = useState<SelectedReviewsData>('all');
  const [nowPageReviews, setNowPageReviews] = useState(Number(searchParams.get('page') || 1));
  const meta = Meta.useMeta();
  const refCharacteristics = useRef<HTMLDivElement>();
  const refReviews = useRef<HTMLDivElement>();
  const refReviewsList = useRef<HTMLDivElement>();
  const refRelatedProducts = useRef<HTMLDivElement>();
  const refAtr = useRef<HTMLDivElement>();
  const refTop = useRef<HTMLDivElement>();
  const refComfortBuy = useRef<HTMLDivElement>();
  const refActionBar = useRef<HTMLDivElement>();
  const featureFlags = useFeatureFlags();
  const modules = PageProductStore.useModules();
  const isAtr = page.isAtr;
  const findYourOwns = page.findYourOwn;
  const qualityLaboratory = page.qualityLaboratory;
  const newCollectionAnnounce = page.newCollectionAnnounce;
  const vozvyshayaComfort = page.vozvyshayaComfort;
  const flowersBlock = page.flowersBlock;
  const idViewerModel = 'pageProduct';
  const ar: AR = page.ar;
  const activeMobilePanel = Configurator.useActiveMobilePanel();
  const selectedPv = Configurator.useSelectedParams();
  const priceblock = useRef<HTMLDivElement>(null);
  const isComingSoon = page.product.status === 'ComingSoon';

  const configurationId = page.configuratorOnPage.configuration.id;
  const { data: configurator } = ConfiguratorQuery.useConfigurator(
    configurationId,
    page.configuratorOnPage,
    [...selectedPv],
  );

  const entityId = 'configurator-textile';

  Filtrator.init(entityId, {
    ...configurator.fabricFilters,
    totalCount: configurator.countFabrics,
    countOfPriorityFilters: 0,
    withoutUrl: true,
  });

  const goodsInfo = useMemo<PecomDeliveryParameters[]>(() => {
    const productParameters = [];
    if (page.isModular) {
      modules.forEach((module) => {
        if (module.count > 0) {
          productParameters.push({
            id: module.id,
            quantity: module.count,
          });
        }
      });
    } else {
      productParameters.push({
        id: page.product.id,
        quantity: 1,
      });
    }
    return productParameters;
  }, [page.isModular, modules, page.product.id]);

  const hasMattressBanner = useMemo(() => {
    return (
      ['matrasy', 'krovati'].includes(page.categoryTranslite) && featureFlags.mattressQuizAvailable
    );
  }, [featureFlags.mattressQuizAvailable, page.categoryTranslite]);

  const needSeparator = !hasMattressBanner && !findYourOwns;

  const siteReviews = useMemo(() => {
    const all = (page.reviewsSubgallery || []).filter((review: ReviewData) => {
      return review.source === 'site';
    });
    const withPhoto = all.filter((review: ReviewData) => review.photos?.length > 0);

    return { all, withPhoto };
  }, [page]);

  const crossSalesTabs = useMemo(() => {
    if (!page?.crossSalesProducts) return [];

    const tabs = [{ id: 'all', label: t('all-categories') }];

    page.crossSalesProducts.products.forEach((p: ProductData) => {
      const tab = tabs.find((tItem) => tItem.id === p.type);

      if (tab) return;

      tabs.push({ id: p.type, label: p.type });
    });

    return tabs;
  }, [page, t]);

  const upSalesTabs = useMemo(() => {
    if (!page?.upSalesProducts) return [];

    const tabs = [{ id: 'all', label: t('all-categories') }];

    page.upSalesProducts.products.forEach((p: ProductData) => {
      const tab = tabs.find((tItem) => tItem.id === p.type);

      if (tab) return;

      tabs.push({ id: p.type, label: p.type });
    });

    return tabs;
  }, [page, t]);

  const filteredCrossSalesProducts = useMemo(() => {
    const cross = page?.crossSalesProducts;

    if (!cross) return [];
    if (selectedCrossSaleTab === 'all') return cross.products;

    return cross.products.filter((p: ProductData) => {
      return p.type === selectedCrossSaleTab;
    });
  }, [page, selectedCrossSaleTab]);

  const filteredUpSalesProducts = useMemo(() => {
    const up = page?.upSalesProducts;

    if (!up) return [];
    if (selectedUpSaleTab === 'all') return up.products;

    return up.products.filter((p: ProductData) => {
      return p.type === selectedUpSaleTab;
    });
  }, [page, selectedUpSaleTab]);

  const mediaGallery = useMemo(() => {
    const images = page.mediaGallery;
    const { image } = configurator.configuration;
    images.grid[0][0].image = image;

    return images;
  }, [page.mediaGallery, configurator.configuration]);

  const scrollToRef = useCallback(
    (ref: MutableRefObject<HTMLDivElement>, correction = 0) => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      let top = window.scrollY + rect.top - correction;

      if (isOnlyMobile && window.scrollY > rect.top) {
        top -= totalHeight;
      } else if (!isOnlyMobile) {
        top -= contentHeight;
      }

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight, contentHeight, isOnlyMobile],
  );
  const handleAddReview = useCallback(() => {
    ModalWindows.open('SendReview', { product: page.product });
  }, [page]);

  const handleChangeCrossSaleTab = useCallback((_e, tab: Tab) => {
    setSelectedCrossSaleTab(tab.id);
  }, []);

  const handleChangeUpSaleTab = useCallback((_e, tab: Tab) => {
    setSelectedUpSaleTab(tab.id);
  }, []);

  const handleClickCharacteristics = useCallback(() => {
    scrollToRef(refCharacteristics, refActionBar.current?.clientHeight);
  }, [scrollToRef]);

  const handleClickReviews = useCallback(() => {
    scrollToRef(refReviews, refActionBar.current?.clientHeight);
  }, [scrollToRef]);

  const handleClickAtr = useCallback(() => {
    scrollToRef(refAtr);
  }, [scrollToRef]);

  const handleChangePage = useCallback(
    (e: MouseEvent, pageNum: number) => {
      setNowPageReviews(pageNum);
      scrollToRef(refReviewsList, refActionBar.current?.clientHeight);
    },
    [scrollToRef],
  );

  const handleClickTop = useCallback(() => {
    priceblock.current = document.getElementById('priceBlock') as HTMLDivElement;

    scrollToRef(priceblock);
  }, [scrollToRef]);

  const handleClickDelivery = useCallback(() => {
    scrollToRef(refComfortBuy, refActionBar.current?.clientHeight);
  }, [scrollToRef]);

  const handleChangeSelectedReviews = useCallback((e: MouseEvent, kind: SelectedReviewsData) => {
    setSelectedReviews(kind);
    setNowPageReviews(1);
  }, []);

  /** Opens send review modal when page is loaded and there is a url parameter */
  useEffect(() => {
    const hasReviewUrlParameter = searchParams.has('review');

    if (!hasReviewUrlParameter) return;

    const orderId = searchParams.get('review');

    ModalWindows.open('SendReview', { product: page.product, orderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    PageProductStore.initViewerModelStore(idViewerModel);

    return () => PageProductStore.clear(idViewerModel);
  }, []);

  const { ref, inView } = useInView({
    rootMargin: '0px 0px',
    threshold: 0,
  });

  const viewActionBar = useMemo(() => {
    if (isMobileM) return !activeMobilePanel;

    return inView;
  }, [inView, isMobileM, activeMobilePanel]);

  useUpdateComponentStorage({
    ...(page.isAvailable &&
      !isComingSoon && {
        buyButton: <StickyBuyButton visible={true} configurator={configurator} />,
      }),
    ...(!page.isAvailable &&
      isComingSoon && {
        saleSoon: <SaleSoonButton visible={true} product={page.product} />,
      }),
  });

  return (
    <div
      {...restProps}
      ref={refTop}
      className={cn(styles.page, className)}
      data-testid='page-product'
    >
      <ActionBar
        ref={refActionBar}
        inView={viewActionBar}
        product={page.product}
        reviews={page.reviewsSubgallery}
        configurator={configurator}
        handleClickTop={handleClickTop}
        handleClickReviews={handleClickReviews}
        handleClickCharacteristics={handleClickCharacteristics}
        handleClickDelivery={handleClickDelivery}
      />

      <div
        className={cn(styles.mainContainer, styles.wrapperMain)}
        {...(page.product.isActive && {
          itemScope: true,
          itemType: 'http://schema.org/Product',
          itemRef: 'reviews-section',
        })}
      >
        <BreadcrumbsWrapper
          className={cn(styles.breadcrumbs, { [styles.outer]: true })}
          breadcrumbs={configurator.configuration.breadcrumbs}
          breadcrumbsSeoData={page.breadcrumbsSeoData}
        />
        <Layout
          className={styles.mainContent}
          cnSidebar={styles.sidebar}
          cnContent={styles.content}
          sidebar={
            !isMobileM && (
              <Sidebar
                visibilityTrackingRef={ref}
                configurator={configurator}
                goodsInfo={goodsInfo}
                onClickReviews={handleClickReviews}
                onClickAtr={handleClickAtr}
              />
            )
          }
        >
          <div>
            <BreadcrumbsWrapper
              className={cn(styles.breadcrumbs, { [styles.inner]: true })}
              breadcrumbs={configurator.configuration.breadcrumbs}
              breadcrumbsSeoData={page.breadcrumbsSeoData}
            />
            <div className={styles.gallery}>
              <PhotoGallery
                key={configurator.configuration.image}
                images={mediaGallery}
                ar={ar}
                product={page.product}
                configuration={configurator.configuration}
                productId={page.product.id}
              />
            </div>

            {isMobileM && (
              <div className={styles.wrapperSidebar}>
                <Sidebar
                  configurator={configurator}
                  goodsInfo={goodsInfo}
                  onClickReviews={handleClickReviews}
                  onClickAtr={handleClickAtr}
                />
              </div>
            )}

            {isAtr && (
              <div ref={refAtr}>
                <ATRInfo className={styles.promoInfo} />
              </div>
            )}

            {newCollectionAnnounce && (
              <div>
                <NewCollectionAnnounce
                  className={styles.promoInfo}
                  atrInfo={newCollectionAnnounce}
                />
              </div>
            )}

            {vozvyshayaComfort && (
              <div>
                <NewCollectionAnnounce className={styles.promoInfo} atrInfo={vozvyshayaComfort} />
              </div>
            )}

            {flowersBlock && (
              <div>
                <NewCollectionAnnounce className={styles.promoInfo} atrInfo={flowersBlock} />
              </div>
            )}
          </div>
        </Layout>
      </div>

      {modules?.length > 0 && <ModulesList modules={modules} className={styles.modules} />}

      <div ref={refCharacteristics} data-onboardid='characteristics' data-testid='characteristics'>
        <AdditionalInfo configurator={configurator} />
      </div>

      {findYourOwns && (
        <ProductsInInterior
          img={findYourOwns.image}
          products={findYourOwns.products}
          title={findYourOwns.title}
          analyticsTitle={findYourOwns.category}
        />
      )}

      {needSeparator && <div className={styles.separator} />}

      <div
        className={cn(styles.wrapperAdditional, { [styles.hasSeparator]: needSeparator })}
        ref={refRelatedProducts}
      >
        {page.crossSalesProducts.products?.length > 0 && (
          <CrossSaleSection
            cnGallery={styles.crossSaleGallery}
            title={page.crossSalesProducts.title}
            analyticsTitle='Вам подойдет'
            products={filteredCrossSalesProducts}
            data-testid='cross-sales-products'
            tabs={
              crossSalesTabs.length > 1 && (
                <ButtonTabs
                  scrollable
                  defaultValue={selectedCrossSaleTab}
                  tabs={crossSalesTabs}
                  onChangeTab={handleChangeCrossSaleTab}
                />
              )
            }
            renderItem={(productCardProps) => (
              <div className={styles.productItem}>
                <CrossSaleProductCard {...productCardProps} />
              </div>
            )}
          />
        )}

        {isMobileM && qualityLaboratory && (
          <QualityLaboratoryBanner
            className={styles.qualityLaboratory}
            qualityLaboratoryData={qualityLaboratory}
          />
        )}

        {page.reviewsSubgallery.length > 0 ? (
          <>
            <div
              className={styles.sectionReviews}
              ref={refReviews}
              data-testid='feedback'
              id='reviews-section'
            >
              <ReviewsSection
                reviews={page.reviewsSubgallery}
                onAddReview={handleAddReview}
                onChangeSelectedReviews={handleChangeSelectedReviews}
              />
              <div className={styles.wrapperListReviews} ref={refReviewsList}>
                <ListReviews
                  className={styles.listReviews}
                  reviews={selectedReviews === 'all' ? siteReviews.all : siteReviews.withPhoto}
                  onChangePage={handleChangePage}
                  nowPage={nowPageReviews}
                />
              </div>
            </div>
            <div className={cn(styles.separator, styles.separatorAdditional)} />
          </>
        ) : (
          <div className={cn(styles.mainContainer, styles.emptyReviewWrapper)} ref={refReviews}>
            <EmptyReview onAddReview={handleAddReview} />
          </div>
        )}

        {page.instagram?.length > 0 && (
          <MainInstagramSection
            title={t('titles.instagram')}
            className={styles.sectionInstagram}
            posts={page.instagram}
            footerText={page.seoPreFooter}
            data-testid='house-furnishing'
          />
        )}

        <div ref={refComfortBuy} />
        {page.deliveryPage && (
          <ErrorBoundary fallback={null}>
            <div className={cn(styles.littleContainer, styles.sectionDelivery)}>
              <DeliverySection
                title={page.deliveryPage.title}
                delivery={page.deliveryPage}
                goodsInfo={goodsInfo}
                data-testid='delivery-assembling'
              />
            </div>
          </ErrorBoundary>
        )}

        <ComfortBuy className={styles.comfortBuy} />

        {page.sameProducts.products?.length > 0 && (
          <CrossSaleSection
            className={styles.sectionSimilar}
            cnGallery={styles.crossSaleGallery}
            title={page.sameProducts.title}
            analyticsTitle={page.sameProducts.title}
            products={page.sameProducts.products}
            data-testid='other-variants'
            renderItem={(productCardProps) => (
              <div className={styles.productItem}>
                <CrossSaleProductCard {...productCardProps} />
              </div>
            )}
          />
        )}

        {page.upSalesProducts.products?.length > 0 && (
          <CrossSaleSection
            className={styles.sectionSimilar}
            cnGallery={styles.crossSaleGallery}
            title={page.upSalesProducts.title}
            analyticsTitle={page.upSalesProducts.title}
            products={filteredUpSalesProducts}
            data-testid='similar-item'
            tabs={
              upSalesTabs.length > 2 && (
                <ButtonTabs
                  scrollable
                  defaultValue={selectedUpSaleTab}
                  tabs={upSalesTabs}
                  onChangeTab={handleChangeUpSaleTab}
                />
              )
            }
            renderItem={(productCardProps) => (
              <div className={styles.productItem}>
                <CrossSaleProductCard {...productCardProps} />
              </div>
            )}
          />
        )}

        {page.historyProducts.products?.length > 0 && (
          <CrossSaleSection
            className={styles.sectionHistory}
            cnGallery={styles.historyGallery}
            title={<span>{t('titles.recently-seen')}</span>}
            analyticsTitle='Вы недавно смотрели'
            products={page.historyProducts.products}
            renderItem={(productCardProps) => (
              <div className={styles.nanoProductItem}>
                <NanoProductCard {...productCardProps} />
              </div>
            )}
          />
        )}
      </div>

      {isCSR &&
        // TODO: meta.data.isOperatorTest - флаг для стресс-теста календаря доставок от 1С. Удалить по окончанию
        (meta.data.isOperatorTest ||
          (!isMobileM && meta.data.isOperator && meta.data.isOperatorPopupShow)) && (
          <ErrorBoundary>
            <Suspense fallback={null}>
              <PanelOperator
                className={cn(styles.operator, {
                  [styles.test]: !!meta.data.isOperatorTest,
                })}
                productId={configurator.configuration.id}
                region={meta.data.region}
              />
            </Suspense>
          </ErrorBoundary>
        )}
    </div>
  );
};

const PageProductContainer: FC = () => {
  const page = PageProductStore.useData();
  const location = useNavigation();

  // Восстанавливаем позицию скролла при клике по тканям базовых коллекций
  useEffect(() => {
    Configurator.changePanelView(true);
    setTimeout(() => {
      Configurator.changePanelView(false);
    }, 3000);
  }, [location?.pathname]);

  useEffect(() => {
    const params = page.configuratorOnPage.parameterValues.filter((pv) => pv.selected);

    Configurator.updateStore(params);
  }, [page.configuratorOnPage]);

  return <PageProduct />;
};

export default PageProductContainer;
